// VIES uses the ISO 3166-1 alpha-2 code as the VAT prefix, except for Greece.
export const VIES_VAT_PREFIX_BY_COUNTRY_CODE = Object.freeze({
  AT: 'AT',
  BE: 'BE',
  BG: 'BG',
  HR: 'HR',
  CY: 'CY',
  CZ: 'CZ',
  DE: 'DE',
  DK: 'DK',
  EE: 'EE',
  GR: 'EL',
  ES: 'ES',
  FI: 'FI',
  FR: 'FR',
  HU: 'HU',
  IE: 'IE',
  IT: 'IT',
  LV: 'LV',
  LT: 'LT',
  LU: 'LU',
  MT: 'MT',
  NL: 'NL',
  PL: 'PL',
  PT: 'PT',
  RO: 'RO',
  SK: 'SK',
  SI: 'SI',
  SE: 'SE',
  XI: 'XI',
})

export const VIES_VAT_PREFIXES = Object.freeze(Object.values(VIES_VAT_PREFIX_BY_COUNTRY_CODE).sort())

const VIES_VAT_NUMBER_PATTERNS_BY_COUNTRY_CODE = Object.freeze({
  AT: /^U\d{8}$/,
  BE: /^\d{10}$/,
  BG: /^\d{9,10}$/,
  HR: /^\d{11}$/,
  CY: /^\d{8}[A-Z]$/,
  CZ: /^\d{8,10}$/,
  DE: /^\d{9}$/,
  DK: /^\d{8}$/,
  EE: /^\d{9}$/,
  GR: /^\d{9}$/,
  ES: /^(?:[A-Z]\d{7}[A-Z0-9]|\d{8}[A-Z]|\d[A-Z]\d{7}[A-Z])$/,
  FI: /^\d{8}$/,
  FR: /^[0-9A-HJ-NP-Z]{2}\d{9}$/,
  HU: /^\d{8}$/,
  IE: /^(?:\d{6}[A-Z]{2}|\d{7}[A-Z]{1,2}|\d[A-Z+*]\d{5}[A-Z])$/,
  IT: /^\d{11}$/,
  LV: /^\d{11}$/,
  LT: /^(?:\d{9}|\d{12})$/,
  LU: /^\d{8}$/,
  MT: /^\d{8}$/,
  NL: /^\d{9}B\d{2}$/,
  PL: /^\d{10}$/,
  PT: /^\d{9}$/,
  RO: /^\d{2,10}$/,
  SK: /^\d{10}$/,
  SI: /^\d{8}$/,
  SE: /^\d{12}$/,
  XI: /^(?:\d{9}|\d{12}|(?:GD|HA)\d{3})$/,
})

const NON_VIES_VAT_NUMBER_PATTERN = /^[A-Z0-9]{2,32}$/

export const getViesVatPrefix = countryCode =>
  VIES_VAT_PREFIX_BY_COUNTRY_CODE[countryCode] || null

export const getCountryCode = countryName => {
  if (!countryName) return null

  const normalizedCountryName = String(countryName).toLowerCase().trim()
  const country =
    normalizedCountryName === 'xi'
      ? countries.find(({ common }) => common === 'Northern Ireland')
      : findCountry(countries, normalizedCountryName)

  if (!country) return null

  return country.common === 'Northern Ireland' ? 'XI' : country.iso2
}

export const getViesVatPrefixForCountry = countryName => {
  return getViesVatPrefix(getCountryCode(countryName))
}

export const normalizeVatNumber = vatNumber =>
  String(vatNumber || '')
    .trim()
    .toUpperCase()
    .replace(/[\s.-]/g, '')

export const hasVatNumberSeparators = vatNumber => /[\s.-]/.test(String(vatNumber || ''))

export const isValidCroatianOIB = oib => {
  if (!/^\d{11}$/.test(oib)) return false

  let remainder = 10
  for (let index = 0; index < 10; index += 1) {
    remainder = (remainder + Number(oib[index])) % 10
    if (remainder === 0) remainder = 10
    remainder = (remainder * 2) % 11
  }

  const checkDigit = (11 - remainder) % 10

  return checkDigit === Number(oib[10])
}

export const isValidVatNumberForCountry = (vatNumber, countryName) => {
  const normalizedVatNumber = normalizeVatNumber(vatNumber)
  const countryCode = getCountryCode(countryName)

  if (!countryCode) return false

  const pattern = VIES_VAT_NUMBER_PATTERNS_BY_COUNTRY_CODE[countryCode]
  if (!pattern) return NON_VIES_VAT_NUMBER_PATTERN.test(normalizedVatNumber)
  if (!pattern.test(normalizedVatNumber)) return false

  return countryCode !== 'HR' || isValidCroatianOIB(normalizedVatNumber)
}

export const getVatNumberMaxLengthForCountry = countryName => {
  const countryCode = getCountryCode(countryName)
  const maximumLengths = {
    AT: 9,
    BE: 10,
    BG: 10,
    HR: 11,
    CY: 9,
    CZ: 10,
    DE: 9,
    DK: 8,
    EE: 9,
    GR: 9,
    ES: 9,
    FI: 8,
    FR: 11,
    HU: 8,
    IE: 9,
    IT: 11,
    LV: 11,
    LT: 12,
    LU: 8,
    MT: 8,
    NL: 12,
    PL: 10,
    PT: 9,
    RO: 10,
    SK: 10,
    SI: 8,
    SE: 12,
  }

  return maximumLengths[countryCode] || 32
}

export const formatViesVatNumber = (vatNumber, prefix) => {
  const normalizedVatNumber = normalizeVatNumber(vatNumber)
  if (!normalizedVatNumber || !prefix || prefix === 'HR' || !VIES_VAT_PREFIXES.includes(prefix)) {
    return normalizedVatNumber
  }

  const countryCode = Object.keys(VIES_VAT_PREFIX_BY_COUNTRY_CODE).find(
    code => VIES_VAT_PREFIX_BY_COUNTRY_CODE[code] === prefix,
  )
  const pattern = countryCode && VIES_VAT_NUMBER_PATTERNS_BY_COUNTRY_CODE[countryCode]
  const vatNumberWithoutPrefix = normalizedVatNumber.slice(prefix.length)

  if (normalizedVatNumber.startsWith(prefix) && pattern && pattern.test(vatNumberWithoutPrefix)) {
    return normalizedVatNumber
  }

  return `${prefix}${normalizedVatNumber}`
}
import { findCountry } from '@/utils'

const countries = require('../assets/countries')
